/**
 * SPEC-077: one PostHog `checkout_error` event for every error a customer is
 * shown at checkout (plans, coupon, payment method, PayPal, submit).
 *
 * Exception autocapture (SPEC-070) only sees uncaught errors; checkout
 * friction is handled errors — a decline, an invalid field, a refused coupon,
 * a failed API call — that our code catches and turns into a message. Those
 * call sites report here.
 *
 * Every property is a fixed code or our own copy. Nothing from the customer
 * or the server's raw body leaves the browser: no card data, nonce, email,
 * address or response body. A server message is only ever *read* to pick a
 * `reason` code; when nothing matches the reason is `other`.
 */
import { capturePosthogEvent } from './posthog'
import planIsOneTimePurchase from '../redux/modules/utils/planOneTimePurchase'

export const CHECKOUT_ERROR_EVENT = 'checkout_error'

const STAGES = ['plan', 'discount', 'payment_method', 'paypal', 'submit']

// Braintree hosted-field keys (error.details.invalidFieldKeys)
const CARD_FIELD_KEYS = ['number', 'expirationDate', 'cvv', 'postalCode']

// The app registers a read-only state getter at boot (index.jsx), so any call
// site can report the plan without threading it through. Until then (tests,
// pages outside the app) the plan is simply unknown.
let readState = () => null
export const setCheckoutErrorContext = (getState) => {
  readState = typeof getState === 'function' ? getState : () => null
}

// Submit attempts within the current checkout: a first-try failure followed
// by a successful retry shows as attempt 1 then attempt 2.
let attempts = 0
export const noteCheckoutAttempt = () => {
  attempts += 1
  return attempts
}
export const resetCheckoutAttempts = () => {
  attempts = 0
}

// Braintree hosted-fields error codes (fixed strings from braintree-web)
const BRAINTREE_CODE_REASONS = {
  HOSTED_FIELDS_FIELDS_INVALID: 'invalid_field',
  HOSTED_FIELDS_FIELDS_EMPTY: 'invalid_field',
  HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE: 'duplicate_card',
  HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED: 'cvv_mismatch',
  HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR: 'network',
  HOSTED_FIELDS_FAILED_TOKENIZATION: 'tokenization_failed'
}

// Server / processor messages are read only to choose a code. Order matters:
// the first match wins.
const MESSAGE_REASONS = [
  [/cvv|security code/i, 'cvv_mismatch'],
  [/postal|zip code|avs|address verification/i, 'avs_mismatch'],
  [/insufficient funds/i, 'insufficient_funds'],
  [/expired card|card (has )?expired|expiration/i, 'card_expired'],
  [
    /declin|do not honor|not authorized|processor declined|card (was )?rejected/i,
    'card_declined'
  ],
  [/fraud|risk|gateway rejected/i, 'gateway_rejected'],
  [/coupon.*expired|expired.*coupon/i, 'coupon_expired'],
  [
    /coupon does not exist|not a valid coupon|coupon is not valid|invalid (coupon|code)/i,
    'coupon_invalid'
  ],
  [
    /only valid for|does not apply|doesn.t apply|not (a )?discountable|does not reduce/i,
    'coupon_not_applicable'
  ],
  [/already (have|own|registered)|already_lifetime/i, 'already_owned'],
  [
    /problems verifying your payment information|payment method was not found/i,
    'payment_method_invalid'
  ],
  [/nonce|payment method/i, 'payment_method_missing'],
  [/payment (gateway|processor)|connecting to the payment/i, 'processor_error']
]

export const reasonFromMessage = (text) => {
  if (typeof text !== 'string' || !text) return 'other'
  const match = MESSAGE_REASONS.find(([pattern]) => pattern.test(text))
  return match ? match[1] : 'other'
}

// A named field anywhere in a (possibly nested) DRF error body, e.g.
// { payment: { error_code: 'card_declined' } }
function findField(value, names, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 3) return undefined
  const direct = names.map((n) => value[n]).find((v) => v != null)
  if (direct != null) return Array.isArray(direct) ? direct[0] : direct
  return Object.values(value)
    .map((v) => findField(v, names, depth + 1))
    .find((v) => v != null)
}

// The whole value of a named list field (findField keeps only the first)
function findAllField(value, names, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 3) return undefined
  const direct = names.map((n) => value[n]).find((v) => v != null)
  if (direct != null) return direct
  return Object.values(value)
    .map((v) => findAllField(v, names, depth + 1))
    .find((v) => v != null)
}

// First human-readable string in a DRF error body — read locally, never sent
function firstText(value) {
  if (!value) return undefined
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return firstText(value.find(Boolean))
  if (typeof value === 'object')
    return [
      value.detail,
      value.non_field_errors,
      value.nonFieldErrors,
      value.error,
      ...Object.values(value)
    ]
      .map(firstText)
      .find(Boolean)
  return undefined
}

// A reason from a fetchHelper failure payload ({ value, status,
// isNetworkError }) — status first, then the server's own wording.
export const reasonFromApiFailure = (payload, fallback = 'other') => {
  if (!payload) return fallback
  if (payload.isNetworkError) return 'network'
  if (payload.status >= 500) return 'server_error'
  const body = payload.value !== undefined ? payload.value : payload
  // The backend's own code wins when it sends one (error_code, a fixed
  // snake_case identifier); otherwise read the wording.
  const serverCode = findField(body, ['error_code', 'errorCode'])
  if (
    typeof serverCode === 'string' &&
    /^[a-z][a-z0-9_]{0,39}$/.test(serverCode)
  )
    return serverCode
  const reason = reasonFromMessage(firstText(body))
  return reason === 'other' ? fallback : reason
}

// Processor code when the API returns one (fixed alphanumeric code only;
// processor_response_text is never read)
const processorCodeFrom = (payload) => {
  const body =
    payload && (payload.value !== undefined ? payload.value : payload)
  return findField(body, ['processor_response_code', 'processorResponseCode'])
}

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const LONG_DIGITS = /\d[\d\s-]{6,}\d/g
const sanitizeMessage = (text) => {
  if (typeof text !== 'string' || !text) return undefined
  return text
    .replace(EMAIL, '[email]')
    .replace(LONG_DIGITS, '[number]')
    .slice(0, 200)
}

const checkoutType = (isSubscribing, plan) => {
  if (!isSubscribing) return 'cart'
  return planIsOneTimePurchase(plan) ? 'lifetime' : 'subscription'
}

const asCode = (value, pattern, max) =>
  typeof value === 'string' && pattern.test(value)
    ? value.slice(0, max)
    : undefined

/**
 * The event's properties: fixed codes only. Exported for the privacy test
 * (AC-2); call sites use trackCheckoutError.
 */
export const buildCheckoutErrorProps = ({
  stage,
  reason,
  httpStatus,
  processorResponseCode,
  braintreeCode,
  gatewayRejectionReason,
  validationCodes,
  invalidFields,
  couponCode,
  messageShown,
  state = readState()
}) => {
  const checkout = (state && state.checkout) || {}
  const plan = checkout.planSelected || null
  const isSubscribing = Boolean(checkout.isSubscribing)
  const props = {
    stage: STAGES.includes(stage) ? stage : 'submit',
    reason: asCode(reason, /^[a-z_]{1,40}$/, 40) || 'other',
    attempt: attempts,
    plan_id: plan && Number.isInteger(plan.id) ? plan.id : null,
    is_lifetime: planIsOneTimePurchase(plan),
    checkout_type: checkoutType(isSubscribing, plan)
  }
  const status = Number(httpStatus)
  if (Number.isInteger(status) && status >= 100 && status <= 599)
    props.http_status = status
  const processor = asCode(
    processorResponseCode == null ? undefined : String(processorResponseCode),
    /^[0-9A-Za-z]{1,8}$/,
    8
  )
  if (processor) props.processor_response_code = processor
  const btCode = asCode(braintreeCode, /^[A-Z][A-Z0-9_]{2,63}$/, 64)
  if (btCode) props.braintree_code = btCode
  // backend's fixed set: cvv, avs, avs_and_cvv, fraud, risk_threshold, duplicate
  const rejection = asCode(gatewayRejectionReason, /^[a-z_]{1,24}$/, 24)
  if (rejection) props.gateway_rejection_reason = rejection
  // Braintree validation codes are numeric; nothing else is kept
  const codes = (Array.isArray(validationCodes) ? validationCodes : [])
    .map(String)
    .filter((code) => /^\d{1,6}$/.test(code))
    .slice(0, 5)
  if (codes.length) props.validation_codes = codes.join(',')
  // Which card fields Braintree flagged: field keys from a fixed set, never
  // what the customer typed
  const fields = (Array.isArray(invalidFields) ? invalidFields : []).filter(
    (key) => CARD_FIELD_KEYS.includes(key)
  )
  if (fields.length) props.invalid_fields = [...new Set(fields)].join(',')
  if (props.stage === 'discount' && typeof couponCode === 'string') {
    const code = couponCode
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, '')
      .slice(0, 32)
    if (code) props.coupon_code = code
  }
  const message = sanitizeMessage(messageShown)
  if (message) props.message_shown = message
  return props
}

// NFR-1: one event per error shown — a re-render or a double handler within
// a moment of the first report is the same error, not a new one.
const DEDUPE_MS = 3000
let lastKey = null
let lastAt = 0

export const trackCheckoutError = (args) => {
  try {
    const props = buildCheckoutErrorProps(args)
    const key = [
      props.stage,
      props.reason,
      props.message_shown,
      props.attempt
    ].join('|')
    const now = Date.now()
    if (key === lastKey && now - lastAt < DEDUPE_MS) return null
    lastKey = key
    lastAt = now
    capturePosthogEvent(CHECKOUT_ERROR_EVENT, props)
    return props
  } catch (_) {
    // analytics must never break the checkout
    return null
  }
}

// An API failure the customer was shown (fetchHelper failure payload)
export const trackApiFailure = (stage, payload, messageShown, extra = {}) =>
  trackCheckoutError({
    stage,
    reason: reasonFromApiFailure(payload, extra.fallbackReason),
    httpStatus: payload && payload.status,
    processorResponseCode: processorCodeFrom(payload),
    gatewayRejectionReason: findField(
      payload && (payload.value !== undefined ? payload.value : payload),
      ['gateway_rejection_reason', 'gatewayRejectionReason']
    ),
    validationCodes: findAllField(
      payload && (payload.value !== undefined ? payload.value : payload),
      ['validation_codes', 'validationCodes']
    ),
    messageShown,
    ...extra
  })

// A Braintree client error (hosted fields tokenization / validation)
export const trackBraintreeError = (stage, error, messageShown) => {
  const code = error && error.code
  return trackCheckoutError({
    stage,
    reason:
      BRAINTREE_CODE_REASONS[code] ||
      reasonFromMessage(error && error.message) ||
      'tokenization_failed',
    braintreeCode: code,
    invalidFields: error && error.details && error.details.invalidFieldKeys,
    messageShown
  })
}

// Test hook: forget the dedupe window and attempt count
export const resetCheckoutErrorTracking = () => {
  lastKey = null
  lastAt = 0
  attempts = 0
}
